import { Button, Group, Kbd, Stack, Text, Textarea } from "@mantine/core";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";

import { coglexPrefixMatches, isCoglexToken } from "../coglex";
import { extractWords, getCurrentWord } from "./sentence-tokens";

const SUGGESTION_LIMIT = 6;

export interface SentenceInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSubmit?: (() => void) | undefined;
  readonly disabled?: boolean;
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform);

export function SentenceInput({
  value,
  onChange,
  onSubmit,
  disabled = false,
}: SentenceInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursor, setCursor] = useState(0);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.currentTarget.value);
      setCursor(e.currentTarget.selectionStart);
    },
    [onChange],
  );

  const handleSelect = useCallback((e: { currentTarget: HTMLTextAreaElement }) => {
    setCursor(e.currentTarget.selectionStart);
  }, []);

  const invalidWords = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const w of extractWords(value)) {
      if (!isCoglexToken(w) && !seen.has(w)) {
        seen.add(w);
        out.push(w);
      }
    }
    return out;
  }, [value]);

  const current = useMemo(() => getCurrentWord(value, cursor), [value, cursor]);
  const suggestions = useMemo(
    () => coglexPrefixMatches(current.partial, SUGGESTION_LIMIT),
    [current.partial],
  );

  // Couple the highlighted suggestion to the partial word so it resets
  // naturally when the user types another letter. Storing `partial`
  // alongside `index` lets the derivation stay pure (no useEffect) —
  // when `partial` no longer matches the current partial, the
  // highlight falls back to index 0.
  const [selection, setSelection] = useState<{ readonly partial: string; readonly index: number }>(
    { partial: "", index: 0 },
  );
  const selectedSuggestionIndex =
    selection.partial === current.partial ? selection.index : 0;
  const selectedSuggestion = suggestions[selectedSuggestionIndex] ?? suggestions[0];

  const insertSuggestion = useCallback(
    (word: string) => {
      const before = value.slice(0, current.start);
      const after = value.slice(current.end);
      // Always append a trailing space unless the next char is already
      // whitespace, so the next word starts cleanly.
      const trailing = after.startsWith(" ") ? "" : " ";
      const next = `${before}${word}${trailing}${after}`;
      onChange(next);
      // Restore focus + place cursor right after the inserted word.
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        const newCursor = current.start + word.length + trailing.length;
        node.setSelectionRange(newCursor, newCursor);
        setCursor(newCursor);
      });
    },
    [current.end, current.start, onChange, value],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelection({
          partial: current.partial,
          index: (selectedSuggestionIndex + 1) % suggestions.length,
        });
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelection({
          partial: current.partial,
          index: (selectedSuggestionIndex - 1 + suggestions.length) % suggestions.length,
        });
        return;
      }
      if (e.key === "Tab" && !e.shiftKey && selectedSuggestion !== undefined) {
        e.preventDefault();
        insertSuggestion(selectedSuggestion);
        return;
      }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <Stack gap="xs">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onKeyUp={handleSelect}
        onKeyDown={handleKeyDown}
        onClick={handleSelect}
        disabled={disabled}
        minRows={4}
        autosize
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder="Compose a sentence using Coglex words…"
        aria-label="sentence"
      />
      <Text size="xs" c="dimmed">
        <Kbd>Tab</Kbd> accepts highlighted suggestion · <Kbd>↑</Kbd> <Kbd>↓</Kbd> navigate ·{" "}
        <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd> <Kbd>↵</Kbd> submit
      </Text>
      {suggestions.length > 0 && (
        <Group gap="xs" aria-label="suggestions">
          {suggestions.map((w, i) => {
            const isSelected = i === selectedSuggestionIndex;
            return (
              <Button
                key={w}
                size="compact-xs"
                variant={isSelected ? "filled" : "default"}
                aria-selected={isSelected}
                onClick={() => {
                  insertSuggestion(w);
                }}
                disabled={disabled}
              >
                {w}
              </Button>
            );
          })}
        </Group>
      )}
      {invalidWords.length > 0 && (
        <Text c="red" size="sm">
          not in Coglex vocabulary: {invalidWords.join(", ")}
        </Text>
      )}
    </Stack>
  );
}
